/**
 * PostHog analytics — init, identify, track, and page views.
 *
 * Loaded once at app startup by `main.tsx`, before React renders, so anything
 * mounted afterwards can assume the SDK is either live or permanently absent
 * (no key configured). Every export is a no-op until then: a dropped event is
 * always preferable to a crash on a page a reader is looking at.
 *
 * **`identifyUser` is for owner sessions only, and that is a privacy boundary
 * rather than a style preference.** The public tree — landing, `/spotlight`,
 * `/trending`, every `/clip/*` page — is readers, not accounts, and the
 * product's stated position for them is counts-not-people. Only the lazy auth
 * island calls this, and `src/test/analytics-exports-called.test.ts` pins the
 * list of files allowed to import it.
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

/**
 * The id this browser is currently identified as, or null.
 *
 * The latch is what makes `identifyUser` safe to call from a mount effect.
 * Each owner surface is its own lazy route wrapping its own `AuthGate`, so the
 * island unmounts and remounts on every navigation between `/me`, `/inbox`,
 * `/analytics` and friends — and under Clerk the token-bridge effect re-runs
 * whenever `getToken`'s identity changes, which is most renders. Without this,
 * "identify on session resolve" would mean an `$identify` on every route
 * change: same person, same id, pure noise on the wire and in the event
 * stream. With it, the second and later calls are a string compare.
 */
let identifiedAs: string | null = null;

export function initAnalytics(): void {
  if (initialized || !POSTHOG_KEY) return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage',
    autocapture: false,
  });
  initialized = true;
}

/**
 * Attaches this browser's events to a Clerk user id.
 *
 * Call it whenever a signed-in owner session resolves; repeats for the same id
 * are dropped, and a *different* id still goes through so switching accounts in
 * one tab lands on the right person.
 *
 * Takes no properties argument on purpose. Nothing needs one, and what we would
 * attach to a named person is a privacy decision that should be made
 * deliberately rather than inherited from a signature nobody chose — an
 * argument-free identify also makes the latch above unambiguous.
 */
export function identifyUser(userId: string): void {
  if (!initialized || !userId) return;
  if (identifiedAs === userId) return;
  identifiedAs = userId;
  posthog.identify(userId);
}

/**
 * Forgets who this browser belongs to. Call on sign-out, always.
 *
 * Without `posthog.reset()` the next person to sign in on a shared device
 * inherits the previous one's distinct id and their events merge into a
 * stranger's profile. Clearing the latch matters for the same reason in
 * reverse: reset mints a fresh anonymous id, so the *same* user signing back in
 * has to be identified again rather than skipped as already-identified.
 */
export function resetAnalytics(): void {
  identifiedAs = null;
  if (!initialized) return;
  posthog.reset();
}

export function track(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

export { posthog };
